/**
 * Quem assina uma mensagem de SAÍDA no chat de atendimento.
 *
 * São duas perguntas, e elas precisam ter UMA resposta só: "dá para confiar no
 * `sender` gravado?" e, quando não dá, "quem é o atendente deste lead?". Elas
 * já viveram copiadas no balão, no avatar e no player de áudio, e cada cópia
 * respondia de um jeito — foi assim que a mesma conversa passou a mostrar ora
 * o vendedor, ora o nome da INSTÂNCIA do WhatsApp, balão a balão.
 *
 * Módulo sem Vue e sem store de propósito: é importado tanto pelos composables
 * quanto por helpers puros (audioIdentity.js), e testá-lo não pode exigir
 * montar a aplicação.
 */

/**
 * A mensagem é uma sincronização da ponte WAME cujo autor real não foi
 * identificado — ou seja, o `sender` gravado NÃO pode ser exibido como autor.
 *
 * Nesse caso o `sender` é o dono do token admin da empresa (o token é
 * compartilhado por toda a empresa; ver Messages::MessageBuilder#wame_author_sender),
 * não quem de fato enviou. Exibi-lo atribui a mensagem a um vendedor que não a
 * mandou — foi assim que um par de figurinhas vindas do celular passou a
 * aparecer como "Lucas" ao ser agrupado, enquanto solto mostrava o nome da
 * instância.
 *
 * @param {object} params
 * @param {object} params.contentAttributes  content_attributes da mensagem
 * @param {string} params.senderType         tipo do sender ("user", "contact"…)
 * @param {object} params.inbox              inbox da conversa
 * @returns {boolean}
 */
export function isUnattributedWameMessage({
  contentAttributes,
  senderType,
  inbox,
}) {
  const isApiInboxUserSender =
    inbox?.channel_type === 'Channel::Api' &&
    String(senderType || '').toLowerCase() === 'user';

  if (!isApiInboxUserSender) return false;

  const isWameSyncedMessage = !!(
    contentAttributes?.wameSync ?? contentAttributes?.wame_sync
  );
  const hasResolvedWameAuthor = !!(
    contentAttributes?.wameAuthorChatwootUserId ??
    contentAttributes?.wame_author_chatwoot_user_id
  );

  return isWameSyncedMessage && !hasResolvedWameAuthor;
}

/**
 * Identidade interna usada pelos avatares das mensagens de IA.
 * Não usar este nome como prefixo textual da bolha; o corpo deve permanecer
 * exatamente como foi escrito e enviado.
 * AgentBots só são normalizados quando usam o nome legado Zorateam; os demais
 * bots mantêm a identidade configurada. Toda resposta do Captain é IA.
 *
 * @param {object} params
 * @param {object} params.contentAttributes content_attributes da mensagem
 * @param {string} params.senderType tipo do remetente
 * @param {string} params.senderName nome configurado no remetente
 * @returns {string|null} "IA" quando é resposta de IA; senão null
 */
const SELLER_AI_DISPLAY_NAME = 'IA';

export function aiReplyDisplayName({
  contentAttributes,
  senderType,
  senderName,
}) {
  if (contentAttributes?.auroraAi ?? contentAttributes?.aurora_ai) {
    return SELLER_AI_DISPLAY_NAME;
  }

  const normalizedType = String(senderType || '').toLowerCase();
  if (normalizedType === 'captain_assistant') return SELLER_AI_DISPLAY_NAME;

  const isLegacyZoraxyBot =
    normalizedType === 'agent_bot' &&
    String(senderName || '')
      .trim()
      .toLowerCase() === 'zorateam';

  return isLegacyZoraxyBot ? SELLER_AI_DISPLAY_NAME : null;
}

/**
 * Atendente responsável pelo lead AGORA — o responsável da conversa.
 *
 * É o rótulo das mensagens de saída que não têm autor identificável: as que
 * saíram do celular ligado à conexão (`wame_sync` sem
 * `wame_author_chatwoot_user_id`). Ninguém do painel as enviou, e o `sender`
 * gravado é o dono do token admin da empresa.
 *
 * Até 31/08/2026 esse caminho caía no NOME DO INBOX ("Financeiro", "Facundo"),
 * ou seja, na INSTÂNCIA do WhatsApp; como o mesmo número recebe também os
 * envios do painel, que trazem autor de verdade, a conversa alternava entre o
 * nome do atendente e o do canal, balão a balão. Depois disso o rótulo passou
 * a ficar vazio — o que trocou a alternância "atendente × instância" pela
 * alternância "atendente × nada". O pedido de 04/09/2026 fecha as duas: quem
 * assina é sempre gente, e a única gente conhecida aqui é o responsável.
 *
 * É o responsável ATUAL, por decisão de produto: numa reatribuição o histórico
 * dessas mensagens passa a exibir o novo responsável, porque não existe
 * registro de quem estava com o celular na mão em cada uma delas. As mensagens
 * COM autor resolvido não passam por aqui e continuam mostrando quem escreveu.
 *
 * @param {object} conversation conversa da mensagem, como está no store
 * @returns {{name: string, src: string}|null} identidade, ou null sem responsável
 */
export function conversationAttendantIdentity(conversation) {
  const assignee = conversation?.meta?.assignee;
  const name = String(assignee?.name ?? assignee?.available_name ?? '').trim();

  if (!name) return null;

  return {
    name,
    src: assignee.thumbnail || assignee.avatar_url || '',
  };
}
